/**
 * Card — the general-purpose container (theme T5b §1).
 *
 * Default is rest-state (elevation-1, border-slate-200).  Pass
 * `interactive` to upgrade hover to elevation-2 + border-slate-300.
 * For risk-report clause cards (red/yellow/green tint + left stripe),
 * use the dedicated ClauseCard in the F7 contracts module.
 *
 * Slice P — glassmorphism cascade. The default `bg-white/95
 * backdrop-blur-sm border-white/60` is invisible over solid white
 * backgrounds but renders the Apple-grade frosted-glass effect over
 * tinted page backgrounds, behind motion-animated cards, and over
 * images. Pass `solid` to opt out (e.g. for e-mail-style printable
 * surfaces where transparency reads as a bug).
 */
import type { HTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';

import { cn } from './cn';

type Density = 'regular' | 'compact';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  interactive?: boolean;
  density?: Density;
  /** Opt out of the default glassmorphism — fall back to solid white. */
  solid?: boolean;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { interactive = false, density = 'regular', solid = false, className, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'duration-DEFAULT rounded-xl shadow-1 transition-[box-shadow,border-color] ease-standard',
        solid
          ? 'border border-slate-200 bg-white'
          : 'border border-white/60 bg-white/95 backdrop-blur-sm',
        density === 'regular' ? 'p-6' : 'p-4',
        interactive && 'hover:border-slate-300 hover:shadow-2',
        // `backdrop-blur` makes every card its own stacking context, so an
        // open list inside one is painted under the next card. A card holding
        // an open list rises above its siblings (still under sticky bars).
        'has-[[data-popover-open]]:relative has-[[data-popover-open]]:z-raised',
        className,
      )}
      {...rest}
    />
  );
});

interface CardSectionProps {
  children: ReactNode;
  className?: string;
}

export function CardHeader({ children, className }: CardSectionProps) {
  return (
    <div className={cn('mb-4 flex items-start justify-between gap-4', className)}>{children}</div>
  );
}

export function CardTitle({ children, className }: CardSectionProps) {
  return <h2 className={cn('text-lg font-semibold text-slate-900', className)}>{children}</h2>;
}

export function CardDescription({ children, className }: CardSectionProps) {
  return <p className={cn('mt-1 text-sm text-slate-600', className)}>{children}</p>;
}

export function CardFooter({ children, className }: CardSectionProps) {
  return (
    <div
      className={cn(
        'mt-6 flex items-center justify-end gap-2 border-t border-slate-200 pt-4',
        className,
      )}
    >
      {children}
    </div>
  );
}
